import React from 'react';
import { Grid, TextField } from '@mui/material';
import { SnackContext } from 'src/store/ContextStore';
import { APICONSTANTS } from 'src/components/Constants';
import AnonymousFreeTextBanner from '../pedigree-chart/AnonymousFreeTextBanner';
import axios from 'src/axios';

export default function FamilyNote({
  user,
  onDirtyChange,
  saveRef,
  discardRef,
  // TGCLOUD-1791 — called with the saved text so the caller can put it back into
  // app state. Without this the component only ever told ITSELF about a save, so
  // anything else rendering `familyNote` — now including the chart header and
  // the export — showed the pre-edit value until the family was reloaded.
  // ExternalFamilyIds was given the same callback for the same reported defect
  // (TGCLOUD-1666).
  onSaved
}) {
  const { setSnack } = React.useContext(SnackContext);

  // Re-seeds when the family changes. This was memoised on `[]`, so the
  // textarea kept the first family's note when the prop changed underneath it —
  // harmless while the note arrived from exactly one place, and a live bug the
  // moment a save dispatches a fresh value back into app state, which `onSaved`
  // now does.
  const initialNote = React.useMemo(
    () => user?.familyNote || '',
    [user?.familyNote]
  );

  const [note, setNote] = React.useState(initialNote);
  // Follow a family switch or an externally-updated note, but never clobber what
  // the user is typing: `initialNote` only changes when the persisted value does.
  const lastSeededRef = React.useRef(initialNote);
  React.useEffect(() => {
    if (lastSeededRef.current === initialNote) return;
    lastSeededRef.current = initialNote;
    setNote(initialNote);
  }, [initialNote]);

  const handleChange = (e) => {
    setNote(e.target.value);
    onDirtyChange?.(true);
  };

  const onSave = async () => {
    await axios
      .put(APICONSTANTS.setFamilyNote.replace(':user_id', user._id), {
        familyNote: note
      })
      .then(({ data }) => {
        const saved = data.familyNote ?? '';
        lastSeededRef.current = saved;
        setNote(saved);
        onDirtyChange?.(false);
        onSaved?.(saved);
        setSnack({
          status: true,
          msg: 'Family note updated',
          severity: 'success'
        });
      })
      .catch(() => {
        setSnack({
          status: true,
          msg: 'An error occurred',
          severity: 'error'
        });
      });
  };

  const onDiscard = () => {
    setNote(initialNote);
    onDirtyChange?.(false);
  };

  React.useEffect(() => {
    if (saveRef) saveRef.current = onSave;
    if (discardRef) discardRef.current = onDiscard;
  });

  return (
    <>
      <AnonymousFreeTextBanner sx={{ mb: 1.5 }} />
      <Grid container spacing={2}>
        <Grid size={12}>
          <TextField
            variant="outlined"
            fullWidth
            multiline
            minRows={3}
            placeholder="Add a note about this family..."
            value={note}
            onChange={handleChange}
          />
        </Grid>
      </Grid>
    </>
  );
}
